import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, CircleCheck as CheckCircle2, Package, Truck, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useLanguageStore } from '@/lib/language-store';
import { useAuthStore } from '@/lib/auth-store';
import { useDomainStore } from '@/lib/domain-store';
import { CantileverBOMSummary } from '@/lib/cantilever-bom';
import { useCantileverStore } from '@/lib/cantilever-store';

interface OrderQuotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bom?: CantileverBOMSummary | null;
}

interface FormData {
  company: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  postalCode: string;
  city: string;
  shippingMethod: 'shipping' | 'pickup';
  notes: string;
}

const emptyForm: FormData = {
  company: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  country: '',
  postalCode: '',
  city: '',
  shippingMethod: 'shipping',
  notes: '',
};

const requiredFields: (keyof FormData)[] = ['company', 'contactName', 'email'];

export function OrderQuotationDialog({ open, onOpenChange, bom: bomProp }: OrderQuotationDialogProps) {
  const [bomExpanded, setBomExpanded] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { getTranslation } = useLanguageStore();
  const { user, reseller } = useAuthStore();
  const { domainReseller } = useDomainStore();
  const config = useCantileverStore((s) => s.config);

  // Use passed bom or fallback to null
  const bom = bomProp ?? null;

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
      setErrors({});
      setSubmitted(false);
      setSubmitError(null);
      setBomExpanded(false);
    }
  }, [open]);

  const handleFieldChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof FormData, boolean>> = {};
    let valid = true;
    for (const field of requiredFields) {
      if (!form[field].trim()) {
        newErrors[field] = true;
        valid = false;
      }
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.email && !emailRegex.test(form.email)) {
      newErrors.email = true;
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError(null);

    const activeResellerId = reseller?.id ?? domainReseller?.id ?? null;

    const payload = {
      reseller_id: activeResellerId,
      user_id: user?.id ?? null,
      company: form.company,
      contact_name: form.contactName,
      email: form.email,
      phone: form.phone || null,
      address: form.address || null,
      country: form.country,
      postal_code: form.postalCode,
      city: form.city,
      shipping_method: form.shippingMethod,
      notes: form.notes || null,
      configuration: config,
      bom_snapshot: bom
        ? {
            items: bom.items.map((i) => ({
              materialId: i.materialId,
              description: i.description,
              quantity: i.quantity,
              unit: i.unit,
              unitPrice: i.unitPrice,
              totalPrice: i.totalPrice,
            })),
            totalPrice: bom.totalPrice,
            totalWeight: bom.totalWeight,
            maxLoadPerArm: bom.maxLoadPerArm,
          }
        : null,
    };

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const submitRes = await fetch(`${supabaseUrl}/functions/v1/submit-quotation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${anonKey}` },
      body: JSON.stringify(payload),
    });

    const submitJson = await submitRes.json();

    if (!submitRes.ok || submitJson.error) {
      setSubmitError(submitJson.error ?? 'Failed to submit quotation');
      setSubmitting(false);
      return;
    }

    const insertedData = submitJson.id ? { id: submitJson.id } : null;
    const activeResellerEmail = domainReseller?.email ?? reseller?.email ?? null;
    const activeResellerName = domainReseller?.name ?? reseller?.name ?? '';

    setSubmitting(false);
    setSubmitted(true);

    if (insertedData && bom) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        await fetch(`${supabaseUrl}/functions/v1/send-quotation-email`, {
          method: 'POST',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${anonKey}` },
          body: JSON.stringify({
            quotationId: insertedData.id,
            resellerEmail: activeResellerEmail,
            customerEmail: form.email,
            customerName: form.contactName,
            company: form.company,
            resellerName: activeResellerName,
            totalPrice: bom.totalPrice,
            bomItems: bom.items.map((i) => ({
              materialId: i.materialId,
              description: i.description,
              quantity: i.quantity,
              unit: i.unit,
              unitPrice: i.unitPrice,
              totalPrice: i.totalPrice,
            })),
            configuration: payload.configuration,
            notes: form.notes || null,
          }),
        });
      } catch {
        // email failure should not block success state
      } finally {
        clearTimeout(timeout);
      }
    }
  };

  const inputClass = (field: keyof FormData) =>
    `w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-900 outline-none transition-colors focus:ring-2 focus:ring-[#212b4f]/30 ${
      errors[field] ? 'border-red-400' : 'border-gray-300 focus:border-[#212b4f]'
    }`;

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{getTranslation('order.success.title')}</h3>
              <p className="text-sm text-gray-600">{getTranslation('order.success.message')}</p>
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-2 bg-[#212b4f] hover:bg-[#1a2240] text-white rounded-2xl px-8"
            >
              {getTranslation('order.cancel')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#212b4f]">
            {getTranslation('order.quotation.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* BOM Section */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setBomExpanded((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-[#212b4f]" />
                <span className="text-sm font-semibold text-gray-800">{getTranslation('order.bom.section')}</span>
                {bom && (
                  <span className="text-xs text-gray-500 ml-1">
                    — €{bom.totalPrice.toFixed(2)} · {bom.totalWeight} kg
                  </span>
                )}
              </div>
              {bomExpanded ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </button>

            {bomExpanded && bom && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-2 font-medium text-gray-600">{getTranslation('bom.material.id')}</th>
                      <th className="text-left p-2 font-medium text-gray-600">{getTranslation('bom.description')}</th>
                      <th className="text-center p-2 font-medium text-gray-600">{getTranslation('bom.unit')}</th>
                      <th className="text-right p-2 font-medium text-gray-600">{getTranslation('bom.qty')}</th>
                      <th className="text-right p-2 font-medium text-gray-600">{getTranslation('bom.unit.price')}</th>
                      <th className="text-right p-2 font-medium text-gray-600">{getTranslation('bom.total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {bom.items.map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="p-2 font-mono text-gray-500">{item.materialId}</td>
                        <td className="p-2 font-medium text-gray-800">{item.description}</td>
                        <td className="p-2 text-center text-gray-600">{item.unit}</td>
                        <td className="p-2 text-right font-semibold text-gray-800">{item.quantity}</td>
                        <td className="p-2 text-right text-gray-600">€{item.unitPrice.toFixed(2)}</td>
                        <td className="p-2 text-right font-semibold text-gray-800">€{item.totalPrice.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                      <td colSpan={5} className="p-2 text-right font-bold text-gray-700 text-xs">{getTranslation('bom.total')}:</td>
                      <td className="p-2 text-right font-bold text-green-600 text-xs">€{bom.totalPrice.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Contact & Delivery Form */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
              <MapPin className="h-4 w-4 text-[#212b4f]" />
              <h4 className="text-sm font-semibold text-gray-800">{getTranslation('order.contact.title')}</h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">
                  {getTranslation('order.company')} <span className="text-red-500">*</span>
                </Label>
                <input type="text" value={form.company} onChange={(e) => handleFieldChange('company', e.target.value)} className={inputClass('company')} placeholder={getTranslation('order.company')} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">
                  {getTranslation('order.contact.name')} <span className="text-red-500">*</span>
                </Label>
                <input type="text" value={form.contactName} onChange={(e) => handleFieldChange('contactName', e.target.value)} className={inputClass('contactName')} placeholder={getTranslation('order.contact.name')} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">
                  {getTranslation('order.email')} <span className="text-red-500">*</span>
                </Label>
                <input type="email" value={form.email} onChange={(e) => handleFieldChange('email', e.target.value)} className={inputClass('email')} placeholder="email@company.com" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">{getTranslation('order.phone')}</Label>
                <input type="tel" value={form.phone} onChange={(e) => handleFieldChange('phone', e.target.value)} className={inputClass('phone')} placeholder="+31 ..." />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-700">{getTranslation('order.address')}</Label>
              <input type="text" value={form.address} onChange={(e) => handleFieldChange('address', e.target.value)} className={inputClass('address')} placeholder={getTranslation('order.address')} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">{getTranslation('order.postal')}</Label>
                <input type="text" value={form.postalCode} onChange={(e) => handleFieldChange('postalCode', e.target.value)} className={inputClass('postalCode')} placeholder="1234 AB" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">{getTranslation('order.city')}</Label>
                <input type="text" value={form.city} onChange={(e) => handleFieldChange('city', e.target.value)} className={inputClass('city')} placeholder={getTranslation('order.city')} />
              </div>
              <div className="space-y-1 col-span-2 sm:col-span-1">
                <Label className="text-xs font-medium text-gray-700">{getTranslation('order.country')}</Label>
                <input type="text" value={form.country} onChange={(e) => handleFieldChange('country', e.target.value)} className={inputClass('country')} placeholder={getTranslation('order.country')} />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-700">{getTranslation('order.shipping.method')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleFieldChange('shippingMethod', 'shipping')}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${form.shippingMethod === 'shipping' ? 'bg-[#212b4f] text-white border-[#212b4f]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#212b4f]'}`}
                >
                  <Truck className="h-4 w-4 flex-shrink-0" />
                  {getTranslation('order.shipping')}
                </button>
                <button
                  type="button"
                  onClick={() => handleFieldChange('shippingMethod', 'pickup')}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${form.shippingMethod === 'pickup' ? 'bg-[#212b4f] text-white border-[#212b4f]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#212b4f]'}`}
                >
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  {getTranslation('order.pickup')}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-700">{getTranslation('order.notes')}</Label>
              <textarea
                value={form.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 outline-none transition-colors focus:ring-2 focus:ring-[#212b4f]/30 focus:border-[#212b4f] resize-none"
                placeholder={getTranslation('order.notes.placeholder')}
              />
            </div>
          </div>

          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">{submitError}</div>
          )}

          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-2xl border-gray-300 text-gray-700">
              {getTranslation('order.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-[#212b4f] hover:bg-[#1a2240] text-white rounded-2xl"
            >
              {submitting ? getTranslation('order.submitting') : getTranslation('order.request.quotation')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
